import React, { useState } from 'react';
import { GoogleDriveService, GoogleDriveFile } from '../../../Services/googleDrive';
import { useGoogleDriveFiles } from '../../../Hooks/useGoogleDriveFiles';
import AdminLayout from '../AdminLayout';
import styles from './Easymove.module.scss';

// Componente Modal simples para exibir a descrição
const Modal = ({ isOpen, onClose, title, children }: { isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode }) => {
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>{title}</h2>
          <button onClick={onClose} className={styles.closeButton}>&times;</button>
        </div>
        <div className={styles.modalBody}>
          {children}
        </div>
      </div>
    </div>
  );
};

const formatDescriptionForModal = (description: string) => {
  if (!description) return null;

  const topics = description.split(/[\u2022\u25E6\u25AA\u25AB]/g).filter(topic => topic.trim());

  if (topics.length <= 1) {
    return <p className={styles.modalFileDescription}>{description}</p>;
  }

  return (
    <div className={styles.modalDescriptionContainer}>
      {topics.map((topic, index) => {
        const trimmedTopic = topic.trim();
        if (!trimmedTopic) return null;

        const lines = trimmedTopic.split('\n').filter(line => line.trim());
        const title = lines[0];
        const content = lines.slice(1).join('\n').trim();

        return (
          <div key={index} className={styles.modalTopicItem}>
            <div className={styles.modalTopicTitle}>
              <span className={styles.modalTopicBullet}>•</span>
              {title}
            </div>
            {content && (
              <div className={styles.modalTopicContent}>
                {content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default function EasyMove() {
  const { files, error, loading, refetch } = useGoogleDriveFiles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalFile, setModalFile] = useState<GoogleDriveFile | null>(null);

  const openModal = (file: GoogleDriveFile) => {
    setModalFile(file);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalFile(null);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <p>Carregando arquivos...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className={styles.error}>
          <div className={styles.errorIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <h3>Erro ao carregar arquivos</h3>
          <p>{error}</p>
          <button onClick={refetch} className={styles.retryButton}>
            Tentar novamente
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.headerSection}>
            <div className={styles.headerContent}>
              <h1 className={styles.title}>EasyMove Downloads</h1>
              <p className={styles.description}>
                Portal oficial de downloads do EasyMove. Aqui você encontra todas as versões disponíveis
                do aplicativo. Faça o download da versão mais recente
                ou acesse versões anteriores conforme sua necessidade.
              </p>
            </div>
          </div>

          {files.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10,9 9,9 8,9"/>
                </svg>
              </div>
              <h3>Nenhum arquivo encontrado</h3>
              <p>Não há arquivos disponíveis para download no momento.</p>
              <button onClick={refetch} className={styles.refreshButton}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23,4 23,10 17,10"/>
                  <polyline points="1,20 1,14 7,14"/>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                </svg>
                Recarregar
              </button>
            </div>
          ) : (
            <div className={styles.filesSection}>
              <div className={styles.filesHeader}>
                <h2>Arquivos Disponíveis</h2>
                <button onClick={refetch} className={styles.refreshButton}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23,4 23,10 17,10"/>
                    <polyline points="1,20 1,14 7,14"/>
                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                  </svg>
                  Atualizar
                </button>
              </div>
              
              <div className={styles.files}>
                {files.map((file: GoogleDriveFile) => (
                  <div key={file.id} className={styles.file}>
                    <div className={styles.fileIcon}>
                      <div className={styles.apkIcon}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14,2 14,8 20,8"/>
                          <path d="M16 13H8"/>
                          <path d="M16 17H8"/>
                          <path d="M10 9H9H8"/>
                        </svg>
                      </div>
                    </div>
                    <div className={styles.info}>
                      <div className={styles.fileNameContainer}>
                        <h3>{file.name}</h3>
                        {file.description && (
                          <button
                            className={styles.infoIcon}
                            onClick={() => openModal(file)}
                            aria-label={`Ver descrição de ${file.name}`}
                            title="Ver descrição"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/>
                              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                              <line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                          </button>
                        )}
                      </div>
                      <div className={styles.meta}>
                        <span className={styles.fileSize}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7,10 12,15 17,10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                          {GoogleDriveService.formatFileSize(file.size)}
                        </span>
                        <span className={styles.fileDate}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                          {GoogleDriveService.formatDate(file.createdTime)}
                        </span>
                      </div>
                    </div>
                    <a
                      href={GoogleDriveService.generateDownloadUrl(file.id)}
                      className={styles.download}
                      download
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7,10 12,15 17,10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Download
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={modalFile ? modalFile.name : ''}
        >
          {modalFile && formatDescriptionForModal(modalFile.description || '')}
        </Modal>
      </div>
    </AdminLayout>
  );
}
